<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import {
  Sun,
  Moon,
  MoonStar,
  Monitor,
  Zap,
  Flame,
  TreePine,
} from 'lucide-vue-next'

type ThemeValue = 'light' | 'dark' | 'midnight' | 'flashbang' | 'hell' | 'forest' | 'system'

const themes = [
  { value: 'system' as ThemeValue, label: 'System', icon: Monitor, bgClass: 'bg-muted', iconClass: 'text-foreground' },
  { value: 'light' as ThemeValue, label: 'Light', icon: Sun, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' },
  { value: 'dark' as ThemeValue, label: 'Dark', icon: Moon, bgClass: 'bg-slate-700', iconClass: 'text-slate-200' },
  { value: 'midnight' as ThemeValue, label: 'Midnight', icon: MoonStar, bgClass: 'bg-indigo-900', iconClass: 'text-indigo-300' },
  { value: 'forest' as ThemeValue, label: 'Forest', icon: TreePine, bgClass: 'bg-emerald-800', iconClass: 'text-emerald-300' },
  { value: 'hell' as ThemeValue, label: 'Hell', icon: Flame, bgClass: 'bg-red-900', iconClass: 'text-orange-400' },
]

// Theme management
const theme = ref<ThemeValue>('system')

onMounted(() => {
  const savedTheme = localStorage.getItem('theme') as ThemeValue | null
  theme.value = savedTheme || 'system'
})

const setTheme = (newTheme: ThemeValue) => {
  theme.value = newTheme
  localStorage.setItem('theme', newTheme)
  
  const root = document.documentElement
  root.classList.remove('dark', 'midnight', 'flashbang', 'hell', 'forest')
  
  if (newTheme === 'system') {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    root.classList.toggle('dark', isDark)
  } else if (newTheme === 'dark') {
    root.classList.add('dark')
  } else if (newTheme === 'midnight') {
    root.classList.add('midnight')
  } else if (newTheme === 'flashbang') {
    root.classList.add('flashbang')
  } else if (newTheme === 'hell') {
    root.classList.add('hell')
  } else if (newTheme === 'forest') {
    root.classList.add('forest')
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Section Header -->
    <div>
      <h3 class="text-sm font-medium">Appearance</h3>
      <p class="text-xs text-muted-foreground mt-1">Customize how the app looks and feels</p>
    </div>
    
    <!-- Theme Selection -->
    <div class="space-y-3">
      <label class="text-xs font-medium text-muted-foreground uppercase tracking-wide">Color Theme</label>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <button
          v-for="t in themes"
          :key="t.value"
          @click="setTheme(t.value)"
          class="group relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all duration-200 hover:border-primary/50"
          :class="[
            theme === t.value 
              ? 'border-primary bg-primary/5' 
              : 'border-border bg-card hover:bg-muted/50'
          ]"
        >
          <div 
            class="w-8 h-8 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            :class="t.bgClass"
          >
            <component :is="t.icon" class="h-4 w-4" :class="t.iconClass" />
          </div>
          <span class="text-xs font-medium">{{ t.label }}</span>
          <div 
            v-if="theme === t.value"
            class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary"
          />
        </button>
      </div>
    </div>
  </div>
</template>
